import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Package, Heart, Truck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api, money } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const { wishlist } = useCart();
  const [orders, setOrders] = useState([]);
  const [saved, setSaved] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) api.get("/my/orders").then(({ data }) => setOrders(data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (wishlist.length > 0) {
      api.get(`/advisor/products?slugs=${wishlist.join(",")}`).then(({ data }) => setSaved(data)).catch(() => {});
    } else setSaved([]);
  }, [wishlist]);

  if (loading || !user) return <div className="py-32 text-center text-black/40 animate-pulse text-sm uppercase tracking-widest font-bold">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-14">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#4A5D23] font-mono">My Account</p>
          <h1 data-testid="account-welcome" className="font-heading text-3xl md:text-4xl uppercase font-black tracking-tighter mt-1">Welcome, {user.name || user.email}</h1>
        </div>
        <button data-testid="account-logout-btn" onClick={() => { logout(); navigate("/"); }} className="border border-black px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-150">Logout</button>
      </div>

      <section className="mt-12">
        <h2 className="font-heading text-xl uppercase font-bold tracking-tight flex items-center gap-2 mb-5"><Package size={18} className="text-[#4A5D23]" /> Order History</h2>
        {orders.length === 0 ? (
          <div className="border border-black/10 p-10 text-center text-sm text-black/50">
            No orders yet. <Link to="/shop" className="text-[#4A5D23] font-bold">Start building your kit →</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.order_id} data-testid={`order-row-${o.order_id}`} className="border border-black/10 p-5 hover:border-[#4A5D23] transition-colors duration-150">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/order/${o.order_id}`} className="font-mono font-bold text-sm hover:text-[#4A5D23] transition-colors duration-150">{o.order_id}</Link>
                      <span data-testid={`order-status-${o.order_id}`} className={`text-[9px] font-bold uppercase tracking-widest px-2 py-1 ${o.status === "delivered" ? "bg-[#2E8B57] text-white" : o.status === "shipped" ? "bg-[#4A5D23] text-white" : o.status === "awaiting_payment" ? "bg-[#B8860B] text-white" : o.status === "cancelled" ? "bg-[#8B0000] text-white" : "bg-[#121212] text-white"}`}>
                        {(o.status || "confirmed").replace(/_/g, " ")}
                      </span>
                    </div>
                    <p className="text-xs text-black/50 mt-1">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} item(s) · {o.payment_method === "usdc" ? "USDC" : "Card"}</p>
                    {o.tracking_number && <p className="text-[11px] text-[#4A5D23] font-bold mt-0.5">{o.carrier || "Carrier"} #<span className="font-mono">{o.tracking_number}</span></p>}
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-bold">{money(o.total)}</p>
                    {o.status === "awaiting_payment" && o.payment_method === "usdc" ? (
                      <Link data-testid={`order-pay-${o.order_id}`} to={`/order/${o.order_id}`}
                        className="bg-[#B8860B] text-white px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest hover:bg-[#8a6508] transition-colors duration-150">
                        Complete Payment
                      </Link>
                    ) : (
                      <Link data-testid={`order-track-${o.order_id}`} to={`/track?order_id=${o.order_id}&email=${encodeURIComponent(o.email || user.email)}`}
                        className="flex items-center gap-1.5 bg-[#121212] text-white px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">
                        <Truck size={13} /> Track
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-xl uppercase font-bold tracking-tight flex items-center gap-2 mb-5"><Heart size={18} className="text-[#4A5D23]" /> Saved Gear</h2>
        {saved.length === 0 ? (
          <div className="border border-black/10 p-10 text-center text-sm text-black/50">Nothing saved yet. Tap the heart on any product.</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{saved.map((p) => <ProductCard key={p.slug} product={p} />)}</div>
        )}
      </section>
    </div>
  );
}
